import { Inject, Injectable } from '@angular/core';
import { CurrentStateService } from '@ddv/behaviors';
import { ApiExecutorService, ApiServices } from '@ddv/http';
import { UserWithType, UserPreferences } from '@ddv/models';
import { Observable, ReplaySubject, Subject, map, shareReplay, switchMap, of } from 'rxjs';

import { FeatureFlagService } from './feature-flag.service';
import { OpenDashboards } from '../models/open-dashboards';
import { Peers } from '../models/peers';

@Injectable({ providedIn: 'root' })
export class UserService {
    /** @deprecated */
    public readonly userPreferences$: Observable<UserPreferences>;

    private readonly userPreferences: Subject<UserPreferences> = new ReplaySubject(1);
    private clientCode: string | undefined;
    private fetchResult: Observable<UserPreferences> | undefined;

    constructor(
        @Inject(ApiServices.ddvMW) private readonly ddvMWService: ApiExecutorService,
        @Inject(ApiServices.ddvApi) private readonly ddvApiService: ApiExecutorService,
        currentState: CurrentStateService,
        private readonly featureFlags: FeatureFlagService,
    ) {
        this.userPreferences$ = this.userPreferences.asObservable();

        currentState.clientCode$
            .pipe(
                switchMap((clientCode) => {
                    // don't do anything if the client code hasn't changed and we already have a result
                    if (this.clientCode === clientCode && this.fetchResult) {
                        return of(undefined);
                    }

                    this.fetchResult = undefined;
                    this.clientCode = clientCode;

                    return this.fetchUserPreferenceData(clientCode);
                }),
            )
            .subscribe({
                next: (userPreferences) => {
                    // because of the early return in the switchMap above we might not need to do anything here
                    if (userPreferences) {
                        this.userPreferences.next(userPreferences);
                    }
                },
            });
    }

    // since we cannot yet get rid of all usages of userPreferences$
    // there is still a race condition between this function and the subscription to clientCode$ on CurrentStateService
    // in theory, this client code will never be different from what has last been emitted by clientCode$
    // so we'll do a check here against that one so we don't make a bunch of API calls to fetch the same set of user preferences
    fetchUserPreferences(clientCode: string): Observable<UserPreferences> {
        if (clientCode === this.clientCode && this.fetchResult) {
            return this.fetchResult;
        }

        this.clientCode = clientCode;
        return this.fetchUserPreferenceData(clientCode, false);
    }

    updateUserPreferenceData(clientCode: string | undefined, userPreferences: UserPreferences): Observable<UserPreferences> {
        const effectiveClientCode = clientCode ?? this.clientCode;
        if (!effectiveClientCode) {
            throw new Error('cannot update user preferences before client code is set from the current state service');
        }

        this.fetchResult = undefined;
        return this.updateUserPreferences(effectiveClientCode, userPreferences);
    }

    // should be a "dashboard list" service
    openDashboard(dashboardId: string): Observable<void | string> {
        if (!this.clientCode) {
            throw new Error('cannot open dashboard before client code is set from the current state service');
        }

        return this.ddvMWService.invokeService<void>(this.clientCode, `user/state/dashboards/open/${dashboardId}`, 'POST');
    }

    // Should be a "dashboard list" service
    updateOpenDashboardOrder(dashboardIdsInOrder: number[]): Observable<void> {
        if (!this.clientCode) {
            throw new Error('cannot updated dashboard order before client code is set from the current state service');
        }

        const clientCode = this.clientCode;
        return this.ddvApiService.invokeServiceWithBody<void>(
            clientCode,
            'user/state/open-dashboards',
            'PUT',
            { openDashboardIds: dashboardIdsInOrder });
    }

    // Should be a "dashboard list" service
    getOpenDashboardOrderedIds(clientCode?: string): Observable<number[]> {
        const effectiveClientCode = clientCode ?? this.clientCode;

        if (!effectiveClientCode) {
            throw new Error('cannot get open dashboard ids before client code is set from the current state service or without explicitly passing one');
        }

        return this.ddvApiService.invokeService<OpenDashboards>(effectiveClientCode, 'user/state/open-dashboards', 'GET')
            .pipe(
                map((openDashboards) => openDashboards.openDashboardIds),
            );
    }

    fetchUserListForPermissions(featureType?: 'DASHBOARD' | 'WIDGET', clientCode?: string): Observable<UserWithType[]> {
        if (!this.clientCode) {
            throw new Error('cannot fetch user list for permissions before client code is set from the current state service');
        }

        const effectiveClientCode = clientCode ?? this.clientCode;
        return this.featureFlags.isFlagEnabled('use-aws-for-sharing', effectiveClientCode)
            .pipe(
                switchMap((useAwsApi) => {
                    if (useAwsApi) {
                        return this.ddvApiService.invokeServiceWithParams<Peers>(effectiveClientCode, '/user/peers')
                            .pipe(
                                map((peers) => {
                                    const users = peers.users.map((user): UserWithType => {
                                        return {
                                            name: user.name,
                                            email: user.email,
                                            internal: user.internal,
                                            type: 'USER',
                                        };
                                    });

                                    const teams = peers.teams.map((team): UserWithType => {
                                        return {
                                            name: team.name,
                                            internal: false,
                                            type: 'TEAM',
                                        };
                                    });

                                    return [...users, ...teams];
                                }),
                            );
                    }

                    const permissionsUrl = featureType === 'DASHBOARD' ? 'user/peers' : 'widgets/peers';
                    return this.ddvMWService.invokeServiceWithParams<UserWithType[]>(effectiveClientCode, permissionsUrl);
                }),
            );
    }

    private fetchUserPreferenceData(clientCode: string, setSharedObservable = true): Observable<UserPreferences> {
        const userPreferences$ = this.ddvApiService.invokeServiceWithParams<Partial<UserPreferences>>(clientCode, 'user/preferences')
            .pipe(
                map((init) => {
                    init.funds = init.fundsCodes?.map((fundCode) => ({ fundCode }));
                    init.clients = init.clientsCodes?.map((cc) => ({ clientCode: cc }));
                    init.clientCode = clientCode;

                    return new UserPreferences(init);
                }),
                shareReplay(1),
            );

        if (setSharedObservable) {
            this.fetchResult = userPreferences$;
        }

        return userPreferences$;
    }

    private updateUserPreferences(clientCode: string, userPreferences: UserPreferences): Observable<UserPreferences> {
        const payload = {
            fundsCodes: userPreferences.fundsCodes,
            clientsCodes: userPreferences.clientsCodes,
            autoDismissErrors: userPreferences.autoDismissErrors,
            startDate: userPreferences.startDate,
            endDate: userPreferences.endDate,
            fundNamesSource: userPreferences.fundNamesSource,
            isFullExport: userPreferences.isFullExport,
        };

        return this.ddvApiService.invokeServiceWithBody<void>(clientCode, 'user/preferences', 'PUT', payload)
            .pipe(
                map(() => {
                    this.userPreferences.next(userPreferences);
                    return userPreferences;
                }),
            );
    }
}
